import { Link } from 'react-router-dom';
import { useGetTopByMinutesQuery } from '../services/leaderboardApi';
import styles from '../css/LeaderboardWidget.module.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMinutes(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min} мин`;
  if (min === 0) return `${h} ч`;
  return `${h} ч ${min} мин`;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonPlayer({ avatarSize, pedestalHeight }) {
  return (
    <div className={styles.skeletonCard}>
      <div
        className={styles.skeletonAvatar}
        style={{ width: avatarSize, height: avatarSize, marginBottom: 10 }}
      />
      <div className={styles.skeletonLine} style={{ width: '65%', height: 12 }} />
      <div className={styles.skeletonLine} style={{ width: '45%', height: 10 }} />
      <div className={styles.skeletonPedestal} style={{ height: pedestalHeight }} />
    </div>
  );
}

function PodiumSkeleton() {
  return (
    <div className={styles.podium}>
      <div className={styles.place2}>
        <SkeletonPlayer avatarSize={72} pedestalHeight={52} />
      </div>
      <div className={styles.place1}>
        <SkeletonPlayer avatarSize={88} pedestalHeight={72} />
      </div>
      <div className={styles.place3}>
        <SkeletonPlayer avatarSize={72} pedestalHeight={36} />
      </div>
    </div>
  );
}

// ─── Player card ──────────────────────────────────────────────────────────────

function PlayerCard({ entry, placeClass }) {
  const isFirst = placeClass === styles.place1;

  return (
    <div className={`${styles.playerCard} ${placeClass}`}>
      <div className={styles.avatarWrap}>
        {isFirst && <span className={styles.crown}>👑</span>}
        <div className={styles.avatarRing}>
          {entry.avatar_url ? (
            <img
              src={entry.avatar_url}
              alt={entry.login || 'Пользователь'}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarFallback}>
              {(entry.login || '?')[0].toUpperCase()}
            </div>
          )}
        </div>
      </div>

      <div className={styles.playerInfo}>
        <span className={styles.login} title={entry.login || 'Пользователь'}>
          {entry.login || 'Пользователь'}
        </span>
        <span className={styles.stat}>{formatMinutes(entry.minutes)}</span>
      </div>

      <div className={styles.pedestal}>{entry.rank}</div>
    </div>
  );
}

// ─── LeaderboardWidget ────────────────────────────────────────────────────────

/**
 * Универсальный виджет пьедестала для главной страницы.
 *
 * Props:
 *   title      — заголовок блока                     (default: 'Топ месяца')
 *   period     — 'current_month' | 'prev_month' | 'all_time'
 *   discipline — 'minutes' | будущие дисциплины       (default: 'minutes')
 *   linkTo     — href кнопки «Все»                   (default: '/top-users')
 */
const LeaderboardWidget = ({
  title = 'Топ месяца',
  period = 'current_month',
  discipline = 'minutes',
  linkTo = '/top-users',
}) => {
  const { data, isLoading } = useGetTopByMinutesQuery(
    { period, limit: 3, offset: 0 },
    { skip: discipline !== 'minutes' },
  );

  const top3 = data?.data?.slice(0, 3) ?? [];
  const [first, second, third] = top3;

  const isEmpty = !isLoading && top3.length === 0;

  return (
    <section className={styles.widget}>
      <div className={styles.widgetHeader}>
        <h2 className={styles.widgetTitle}>{title}</h2>
        <Link to={`${linkTo}?period=${period}`} className={styles.viewAll}>
          Все
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {isLoading ? (
        <PodiumSkeleton />
      ) : isEmpty ? (
        <p className={styles.empty}>Нет данных за выбранный период</p>
      ) : (
        <div className={styles.podium}>
          {second && <PlayerCard entry={second} placeClass={styles.place2} />}
          {first  && <PlayerCard entry={first}  placeClass={styles.place1} />}
          {third  && <PlayerCard entry={third}  placeClass={styles.place3} />}
        </div>
      )}
    </section>
  );
};

export default LeaderboardWidget;
